import { useState } from 'react';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import ToolLayout from '../../components/dev-tools/ToolLayout';
import { copyToClipboard } from '../../utils/clipboard';
import * as jose from 'jose';

export default function JwtDecode() {
  const [input, setInput] = useState('');
  const [header, setHeader] = useState('');
  const [payload, setPayload] = useState('');
  const [error, setError] = useState('');
  const [copiedHeader, setCopiedHeader] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [secret, setSecret] = useState('');
  const [verificationResult, setVerificationResult] = useState<string | null>(null);

  const decodeJwt = () => {
    try {
      const parts = input.trim().split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format. JWT must have 3 parts separated by dots.');
      }

      const decodedHeader = JSON.parse(atob(parts[0]));
      const decodedPayload = JSON.parse(atob(parts[1]));

      setHeader(JSON.stringify(decodedHeader, null, 2));
      setPayload(JSON.stringify(decodedPayload, null, 2));
      setError('');
      setVerificationResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JWT token');
      setHeader('');
      setPayload('');
      setVerificationResult(null);
    }
  };

  const verifySignature = async () => {
    if (!secret.trim()) {
      setVerificationResult('Please enter a secret key');
      return;
    }

    try {
      const secretKey = new TextEncoder().encode(secret);
      await jose.jwtVerify(input.trim(), secretKey);
      setVerificationResult('✓ Signature is valid');
    } catch (err) {
      setVerificationResult(`✗ Signature verification failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleCopyHeader = async () => {
    if (header) {
      const success = await copyToClipboard(header);
      if (success) {
        setCopiedHeader(true);
        setTimeout(() => setCopiedHeader(false), 2000);
      }
    }
  };

  const handleCopyPayload = async () => {
    if (payload) {
      const success = await copyToClipboard(payload);
      if (success) {
        setCopiedPayload(true);
        setTimeout(() => setCopiedPayload(false), 2000);
      }
    }
  };

  return (
    <ToolLayout
      title="JWT Decoder"
      description="Decode and verify JSON Web Tokens (JWT)"
    >
      <div className="space-y-6">
        {/* Security Warning */}
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="flex gap-3">
            <AlertTriangle className="flex-shrink-0 text-yellow-600" size={20} />
            <div className="text-sm text-yellow-800">
              <strong>Security Note:</strong> Verification happens client-side in your browser. We don't collect any data.
            </div>
          </div>
        </div>

        {/* Input */}
        <div className="card">
          <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">JWT Token</h3>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste your JWT token here...&#10;&#10;Example:&#10;eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
            className="mb-4 h-32 w-full rounded-lg border border-gray-300 bg-white p-4 font-mono text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-primary-400"
          />

          <button onClick={decodeJwt} className="btn-primary">
            Decode JWT
          </button>

          {error && (
            <div className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-300">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        {/* Decoded Output */}
        {(header || payload) && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Header */}
            <div className="card">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-50">Header</h3>
                <button
                  onClick={handleCopyHeader}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-950"
                >
                  {copiedHeader ? <Check size={16} /> : <Copy size={16} />}
                  {copiedHeader ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-auto rounded-lg border border-gray-300 bg-gray-50 p-4 font-mono text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                {header}
              </pre>
            </div>

            {/* Payload */}
            <div className="card">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-50">Payload</h3>
                <button
                  onClick={handleCopyPayload}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-950"
                >
                  {copiedPayload ? <Check size={16} /> : <Copy size={16} />}
                  {copiedPayload ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-auto rounded-lg border border-gray-300 bg-gray-50 p-4 font-mono text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                {payload}
              </pre>
            </div>
          </div>
        )}

        {/* Signature Verification */}
        {header && (
          <div className="card">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">Signature Verification</h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Enter your secret key to verify the JWT signature (HMAC algorithms only)
            </p>

            <div className="flex gap-3">
              <input
                type="password"
                value={secret}
                onChange={e => setSecret(e.target.value)}
                placeholder="Enter secret key"
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:border-primary-400"
              />
              <button onClick={verifySignature} className="btn-primary">
                Verify
              </button>
            </div>

            {verificationResult && (
              <div
                className={`mt-4 rounded-lg p-3 text-sm ${
                  verificationResult.startsWith('✓')
                    ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
                    : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
                }`}
              >
                {verificationResult}
              </div>
            )}
          </div>
        )}
      </div>
    </ToolLayout>
  );
}
